// On-demand loader for heavy third-party scripts.
//
// The pose-detection libraries (TensorFlow.js + PoseNet + MediaPipe) weigh
// ~1.5 MB but are ONLY needed by the Yoga pages. They used to sit in
// index.html, so every student downloaded them on every visit — a painful
// cost on a slow mobile connection. Now the Yoga routes pull them in just
// before the page opens.

const cache = new Map();

// Load one script once; repeat calls share the same promise.
export function loadScript(src) {
  if (cache.has(src)) return cache.get(src);
  const p = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    // Only a tag we know FINISHED loading can be reused.
    if (existing && existing.dataset.loaded === '1') { resolve(); return; }
    // A leftover tag that never finished is unusable: its load/error events
    // already fired, so listeners attached now would never run and this
    // promise would hang forever — which used to make the router guard on
    // /yoga wait indefinitely and left the page unreachable for the session.
    if (existing) existing.remove();

    const el = document.createElement('script');
    el.src = src;
    el.async = false; // preserve execution order within a batch
    let settled = false;
    const done = (ok, err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (ok) { el.dataset.loaded = '1'; resolve(); return; }
      el.remove();       // so the next attempt issues a fresh request
      cache.delete(src); // and isn't served this rejected promise
      reject(err);
    };
    // Never let a stalled CDN block navigation indefinitely.
    const timer = setTimeout(() => done(false, new Error(`Timed out loading ${src}`)), 20000);
    el.addEventListener('load', () => done(true));
    el.addEventListener('error', () => done(false, new Error(`Failed to load ${src}`)));
    document.head.appendChild(el);
  });
  cache.set(src, p);
  return p;
}

const POSE_SCRIPTS = [
  'https://cdn.jsdelivr.net/npm/@mediapipe/pose@0.5/pose.js',
  'https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils/drawing_utils.js',
  'https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js',
  'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.10.0/dist/tf.min.js',
  'https://cdn.jsdelivr.net/npm/@tensorflow-models/posenet@2.2.2/dist/posenet.min.js'
];

/**
 * Ensure the pose-detection stack is on the page.
 * Loaded in order so MediaPipe's helpers see their base library.
 * Resolves even on failure — the Yoga views already handle a missing
 * `window.Pose` by showing their own friendly error.
 */
export async function loadPoseLibs() {
  if (window.Pose && window.Camera && window.posenet) return true;
  try {
    for (const src of POSE_SCRIPTS) await loadScript(src);
    return true;
  } catch (e) {
    console.warn('[lazyScripts] pose libraries failed to load:', e.message);
    return false;
  }
}
